import { device } from 'utils/device';
import { SectionType, DepartmentType, PixiResourceType } from 'app/data/Enums';
import { Events } from 'core/events/Events';
import { CoreEvents } from 'core/events/CoreEvents';
import { ImageLoader } from 'core/loaders/ImageLoader';
import { HTMLLoader } from 'core/loaders/HTMLLoader';

import { LoaderGroup } from 'core/loaders/LoaderGroup';
import { PixiLoader } from 'core/loaders/PixiLoader';

import { PixiResourceLoadEvent } from 'app/events/PixiResourceLoadEvent';
import { AjaxHTMLLoadedEvent } from 'app/events/AjaxHTMLLoadedEvent';

import { PreloadPlanDeps } from './preload.types';

/**
 * Load-order construction side of the preloader: which loader groups get
 * built for which section/deeplink (home / info / work / project), the pixi
 * init-vs-background bundles, the HTML loader sets, group progression
 * and the loader-completion event dispatches (PixiResourceLoadEvent,
 * AjaxHTMLLoadedEvent, CoreEvents.DOM_UPDATED) that fire straight off
 * loader callbacks. The boot flow that decides WHEN loading starts (the
 * _doPreloader timers and the opening-complete join) stays in
 * PreloadController.
 *
 * NOTE: the section JSON configs (home / screens / spaces / systems) are no
 * longer fetched here — they are bundled TypeScript now (HomeConfig +
 * views/work/config) and handed to the controllers by
 * AppController._setSectionConfigs during init, so the core loader group
 * joins only on the real network loads (the pixi spritesheets).
 */
export class PreloadPlan {
	private _deps: PreloadPlanDeps;

	private _loaderGroups: Array<LoaderGroup> = [];
	private _loaderGroupIndex: number = 0;

	private _coreLoadComplete: boolean = false;

	private _HTMLTemplates: any = {};


	constructor(deps: PreloadPlanDeps) {
		this._deps = deps;
	}

	/** Backs PreloadController.viewReady */
	public get coreLoadComplete(): boolean {
		return this._coreLoadComplete;
	}

	//Set a loaded HTML template:
	public setHTML(section: string, element: Element): void {
		element.innerHTML = this._HTMLTemplates[section];
	}

	//Loaders
	//(Maybe these should be automated through attributes)
	//-------------------------------------------------------------------------

	public setPreloader(): void {
		if (this._deps.getSection() === SectionType.HOME) {
			this._createHomeLoadOrder();
		} else if (this._deps.getSection() === SectionType.ABOUT) {
			this._createInfoLoadOrder();
		} else if (this._deps.getSection() === SectionType.WORK) {
			this._createWorkLoadOrder(this._deps.getSubsection() as DepartmentType);
		} else if (this._deps.getSection() === SectionType.PROJECT) {
			this._createProjectLoadOrder(this._deps.getSubsection() as DepartmentType);
		}

		//Start primary group loading
		this._loaderGroups[0].load();
	}

	/** Create Home loader - User is at / on top level */
	private _createHomeLoadOrder(): void {
		//1.) Load backgrounds, PIXI home particles, moth, etc so home can be shown:
		this._loaderGroups.push(this._createCoreLoaderGroup(SectionType.HOME));

		//2.) Create a secondary loader group(s) to load all supporting HTML required:
		this._createAllHtmlLoaders();
	}

	private _createAllHtmlLoaders() {
		this._loaderGroups.push(
			this._createHTMLLoaderGroup(
				[
					'/',
					'/info',
					'/studio',
					'/jobs',
					'/contact',
					'/what-we-do',
					'/our-process',
					'/screens',
					'/spaces',
					'/systems'
				],
				'home-html'
			)
		);

		this._loaderGroups.push(this._createHTMLLoaderGroup(this._getAllProjectURLs(), 'projects-html'));
	}

	/**
	 * //Create a pixi loader. This will add the HOME or WORK elements into the intial loader and the other elements into the background loading.
	 * @param section
	 * @returns
	 */
	private _createPixiLoader(section: SectionType): PixiLoader {
		let pixiLoader = new PixiLoader();
		pixiLoader.resources = this._createPixiLoaderInitBundle(section);
		pixiLoader.backgroundResources = this._createPixiLoaderBackgroundBundle(section);
		pixiLoader.id = 'pixi-home';
		pixiLoader.onResourcesReady = this._onPixiInitLoaderReady.bind(this);
		pixiLoader.onBackgroundResourcesReady = this._onPixiLoaderBackgroundReady.bind(this);
		return pixiLoader;
	}

	private _createPixiLoaderInitBundle(section: SectionType): Array<any> {
		if (section == SectionType.HOME) {
			return this._homePixiBundle();
		}

		if (section == SectionType.WORK) {
			return this._departmentsPixiBundle();
		}

		return [];
	}

	private _createPixiLoaderBackgroundBundle(section: SectionType): Array<any> {
		if (section == SectionType.WORK) {
			return this._homePixiBundle();
		}

		if (section == SectionType.HOME) {
			return this._departmentsPixiBundle();
		}

		return this._homePixiBundle().concat(this._departmentsPixiBundle());
	}

	private _homePixiBundle(): Array<any> {
		return [{ ref: 'home-particles', path: `/img/home/spritesheet/home-particles.json`, isSpriteSheet: false }];
	}

	private _departmentsPixiBundle(): Array<any> {
		let textureSize: string = window.innerWidth < 900 ? '@0.5' : '@1.0';

		return [
			{
				ref: `screens-bg`,
				path: `/img/work/spritesheet/work-backgrounds-screens${textureSize}.json`,
				isSpriteSheet: false
			},
			{
				ref: 'spaces-bg',
				path: `/img/work/spritesheet/work-backgrounds-spaces${textureSize}.json`,
				isSpriteSheet: false
			},
			{
				ref: 'systems-bg',
				path: `/img/work/spritesheet/work-backgrounds-systems${textureSize}.json`,
				isSpriteSheet: false
			},
			{ ref: 'work-particles', path: `/img/work/spritesheet/work-particles${textureSize}.json`, isSpriteSheet: false },
			{
				ref: 'work-particles-32',
				path: `/img/work/spritesheet/work-particles_32${textureSize}.json`,
				isSpriteSheet: false
			}
		];
	}

	/**
	 * Load all the items required for a HOME or screens/spaces/systems load.
	 * (The section JSON configs used to load here too — they are bundled
	 * TypeScript now, so the group joins on the pixi spritesheets only.)
	 * @param section
	 * @returns LoaderGroup
	 */
	private _createCoreLoaderGroup(section: SectionType = SectionType.HOME): LoaderGroup {
		//At home preload the Home particles/assets 1st then load as
		let pixiLoader = this._createPixiLoader(section);

		let loaderGroup = new LoaderGroup();
		loaderGroup.id = 'core';
		loaderGroup.add(pixiLoader);
		loaderGroup.onComplete = this._onLoaderGroupComplete.bind(this);
		loaderGroup.onProgress = this._onLoaderGroupProgress.bind(this);
		loaderGroup.load();

		return loaderGroup;
	}

	/**
	 * Create Info Loader order
	 * @param {DepartmentType}
	 */
	private _createWorkLoadOrder(department: DepartmentType): void {
		this._loaderGroups.push(this._createCoreLoaderGroup(SectionType.WORK));
		this._createAllHtmlLoaders();
	}

	/** Create work / section loader */

	/**Create Info Loader order */
	private _createInfoLoadOrder(): void {
		this._backgroundNonCorePreload();
	}

	private _backgroundNonCorePreload() {
		this._loaderGroups.push(this._createCoreLoaderGroup(SectionType.ABOUT));
		this._createAllHtmlLoaders();
	}

	//Load the essential HTML used across the site:
	//Note not all HTML is loaded into the DOM straight away

	//Create html loader:
	private _createHTMLLoader(id: string, url: string, element: Element = null): HTMLLoader {
		let layout = device.isPortraitDevice ? 'mobile' : 'desktop';
		let loader = new HTMLLoader();
		loader.id = id;
		loader.url = url + `?headless=1&layout=${layout}`;
		loader.element = element;
		loader.onComplete = this._onHTMLLoaderComplete.bind(this);
		return loader;
	}

	/**Load a load page templates (store as varibles so they can ve quickly injected) */
	private _createHTMLLoaderGroup(urls: Array<string>, groupId: string = null): LoaderGroup {
		let htmlLoaderGroup: LoaderGroup = new LoaderGroup();
		htmlLoaderGroup.id = groupId;
		urls.forEach((htmlUrl) => {
			let loader = this._createHTMLLoader(htmlUrl, htmlUrl);
			htmlLoaderGroup.add(loader);
		});
		htmlLoaderGroup.onComplete = this._onLoaderGroupComplete.bind(this);
		return htmlLoaderGroup;
	}

	/** Get all project URLS (so HTML can be AJAX loaded) */
	private _getAllProjectURLs(): Array<string> {
		return [
			'/project/march-for-giants',
			'/project/march-for-giants-s',
			'/project/market-masters',
			'/project/toy-town',
			'/project/total-darkness',
			'/project/ready-player-one',
			'/project/anish-kapoor-into-yourself-fall',
			'/project/tracing-the-sky',
			'/project/tend',
			'/project/immersive-gamebox'
		];
	}


	private _createProjectLoadOrder(department: DepartmentType): void {
		//Load all the images on a project:
		let imageLoader = new ImageLoader();
		imageLoader.element = document.querySelectorAll('.project img');

		let loaderGroup = new LoaderGroup();
		loaderGroup.id = 'Project';
		loaderGroup.add(imageLoader);
		loaderGroup.onComplete = this._onLoaderGroupComplete.bind(this);
		loaderGroup.onProgress = this._onLoaderGroupProgress.bind(this);
		this._loaderGroups.push(loaderGroup);

		this._backgroundNonCorePreload();
	}


	//Loader group progress, used to update the main loader
	private _onLoaderGroupProgress(data, loader): void {
		this._deps.onGroupProgress(data.ratio);
	}

	//A loader group is complete:
	private _onLoaderGroupComplete(id: string): void {
		//Check if opening preload has been completed
		if (id == 'core') {
			this._coreLoadComplete = true;
		}

		//Check if there is another loader group to load carry on loading
		this._loadNextGroup();
	}

	/** If there is a new group qued then load */
	private _loadNextGroup(): void {
		setTimeout(() => {
			this._loaderGroupIndex += 1;
			if (this._loaderGroupIndex < this._loaderGroups.length && this._loaderGroups[this._loaderGroupIndex]) {
				this._loaderGroups[this._loaderGroupIndex].load();
			}
		}, this._deps.getGroupLoadDelay());
	}

	//Pixi Loader is ready:
	private _onPixiInitLoaderReady(loaderId: string, resources: any): void {
		//Home parallax particles:
		Events.dispatch(
			new PixiResourceLoadEvent({
				type: PixiResourceType.INIT,
				resource: resources
			})
		);
	}

	//Background PIXI loading ready
	private _onPixiLoaderBackgroundReady(loaderId: string, resources: any) {
		Events.dispatch(
			new PixiResourceLoadEvent({
				type: PixiResourceType.BACKGROUND,
				resource: resources
			})
		);
	}

	//HTML loader ready:
	private _onHTMLLoaderComplete(id: string, loader: HTMLLoader): void {
		//If there is no element then store the HTML so it can be set when required:
		Events.dispatch(new AjaxHTMLLoadedEvent({ loader: loader }));

		if (!loader.element) {
			this._HTMLTemplates[loader.id] = loader.html;
		} else {
			Events.dispatch(CoreEvents.DOM_UPDATED);
		}

		//Destroy the loader as it's not needed anymore:
		loader.destroy();
	}
}
